/**
 * User Settings State Management
 * Redux slice for managing user preferences and settings
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/shared/providers/store';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface BeamPalette {
  [key: string]: string;
}

export interface UserSettingsState {
  useCustomBeamColors: boolean;
  beamPaletteLight: BeamPalette;
  beamPaletteDark: BeamPalette;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: UserSettingsState = {
  useCustomBeamColors: false,
  beamPaletteLight: {},
  beamPaletteDark: {},
};

// =============================================================================
// SLICE
// =============================================================================

export const userSettingsSlice = createSlice({
  name: 'userSettings',
  initialState,
  reducers: {
    setUseCustomBeamColors: (state, action: PayloadAction<boolean>) => {
      state.useCustomBeamColors = action.payload;
    },
    setBeamPaletteLight: (state, action: PayloadAction<BeamPalette>) => {
      state.beamPaletteLight = action.payload;
    },
    setBeamPaletteDark: (state, action: PayloadAction<BeamPalette>) => {
      state.beamPaletteDark = action.payload;
    },
    resetUserSettings: (state) => {
      Object.assign(state, initialState);
    },
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const {
  setUseCustomBeamColors,
  setBeamPaletteLight,
  setBeamPaletteDark,
  resetUserSettings,
} = userSettingsSlice.actions;

// =============================================================================
// SELECTORS
// =============================================================================

export const selectUseCustomBeamColors = (state: RootState) => state.userSettings?.useCustomBeamColors ?? false;
export const selectBeamPaletteLight = (state: RootState) => state.userSettings?.beamPaletteLight ?? {};
export const selectBeamPaletteDark = (state: RootState) => state.userSettings?.beamPaletteDark ?? {};

// =============================================================================
// REDUCER (default export)
// =============================================================================

export default userSettingsSlice;
